// Libraries
import { useState, useEffect, useMemo, Fragment } from "react";
// React Query
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// MUI
import { Stack, Divider, Box } from "@mui/material";

// Hooks
import { usePayrollMappingsContext } from "@/Settings/context/PayrollMapping";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useSnackbar } from "@/Common/hooks/Snackbar/useSnackbar";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
// Constants
import ACTIONS from "@/Constants/actionsList";
import { PAYROLL_MAPPING_KEYS } from "@/Settings/Constants/PayrollMapping";
import { DATE_FORMAT } from "@/Constants/date";

// API
import { getMappedPayItems, getPayItemOptionsByLocationId } from "@/Settings/api/PayrollMappings/queries";
import { updateMappedPayItems, updatePayItemsOptions } from "@/Settings/api/PayrollMappings/mutations";

// Types
import { IMappedPayItem, ISystemSettingsMappedPayItem, IUpdatePayItemsOptionsResponse } from "@/Settings/types/PayrollMapping/api";

// Components
import PayItemsSkeleton from "./Skeleton";
import HeaderSection from "./HeaderSection";
import MappingSection from "./MappingSection";
import CountDownButton from "@/Layouts/components/Buttons/CountDownButton";
import TextIconMessage from "@Layouts/components/Basics/TextIconMessage";

// Helpers
import { areAllInputsFulfilled, getSubmissionPayItems } from "@/Settings/helpers/PayrollMapping";
import { getLocalDate } from "@/Common/helpers/Dates";

// Redux
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
type TPayItemsProps = {
	isActiveTab: boolean;
};

export default function PayItems(props: Readonly<TPayItemsProps>) {
	const { isActiveTab } = props;
	const { locationId = "" } = usePayrollMappingsContext();
	const queryClient = useQueryClient();
	const { showSnackBar } = useSnackbar();

	const { useAuth } = useAuthorization();
	const editAccess = useAuth(ACTIONS.EDIT_PAY_ITEMS_MAPPING);

	const { getSubscription } = useSubscriptions();
	const timezone = useSelector((state: RootState) => state.location.location?.timezone);

	/******************************************* STATES *******************************************/
	const [allMappedPayItems, setAllMappedPayItems] = useState<ISystemSettingsMappedPayItem[]>([]);
	const [updatedAt, setUpdatedAt] = useState<string | null>(null);
	/******************************************* QUERIES *******************************************/
	const { data: payItemsOptions = [], isLoading: isLoadingPayItemOption } = useQuery({
		queryKey: [PAYROLL_MAPPING_KEYS.QUERY.PAY_ITEMS_OPTIONS, locationId],
		queryFn: () => getPayItemOptionsByLocationId(locationId),
		enabled: Boolean(locationId) && isActiveTab,
	});
	const { data: mappedPayItems = [], isLoading: isLoadingMappedPayItems } = useQuery({
		queryKey: [PAYROLL_MAPPING_KEYS.QUERY.MAPPED_PAY_ITEMS, locationId],
		queryFn: async () => {
			const response = await getMappedPayItems(locationId);
			return response?.mappedPayItems ?? [];
		},
		enabled: Boolean(locationId) && isActiveTab,
	});

	/******************************************* MUTATIONS *******************************************/
	const { mutate: applyMapping, isPending: isPendingApplyingMapping } = useMutation({
		mutationFn: (input: Omit<ISystemSettingsMappedPayItem, "_id" | "effectOnPay">[]) => updateMappedPayItems(locationId, input),
		onSuccess: (data) => {
			showSnackBar("Pay Item mapping applied");
			const { mappedPayItems: newMappedPayItems } = data as IMappedPayItem;
			// Update both current and original state with the response from the mutation
			setAllMappedPayItems(newMappedPayItems);

			// Update the query cache
			queryClient.setQueryData([PAYROLL_MAPPING_KEYS.QUERY.MAPPED_PAY_ITEMS, locationId], newMappedPayItems);
		},
		onError: (_error) => {
			showSnackBar("System error.", true);
		},
	});

	const { mutate: updatePayItemsOptionsMutation, isPending: isUpdatingPayItems } = useMutation({
		mutationFn: () => updatePayItemsOptions(locationId),
	});

	/******************************************* SUBSCRIPTIONS ***************************************/
	const mappedPayItemsSubscription = getSubscription([PAYROLL_MAPPING_KEYS.SUBSCRIPTION.MAPPED_PAY_ITEMS, locationId]);
	mappedPayItemsSubscription?.subscriptions?.onUpdateMappedPayItems?.setCallback(
		(response: { data: { onUpdateMappedPayItems: IMappedPayItem } }) => {
			const { mappedPayItems: responseMappedPayItems = [] } = response.data?.onUpdateMappedPayItems ?? {};
			// Update query cache
			queryClient.setQueryData([PAYROLL_MAPPING_KEYS.QUERY.MAPPED_PAY_ITEMS, locationId], responseMappedPayItems);
		}
	);

	const payItemsOptionsSubscription = getSubscription([PAYROLL_MAPPING_KEYS.SUBSCRIPTION.PAY_ITEMS_OPTIONS, locationId]);
	payItemsOptionsSubscription?.subscriptions?.onUpdatePayItemsOptions?.setCallback(
		(response: { data: { onUpdatePayItemsOptions: IUpdatePayItemsOptionsResponse } }) => {
			const { updatedAt: newUpdatedAt } = response.data?.onUpdatePayItemsOptions ?? {};
			setUpdatedAt(newUpdatedAt);
			queryClient.invalidateQueries({
				queryKey: [PAYROLL_MAPPING_KEYS.QUERY.PAY_ITEMS_OPTIONS, locationId],
			});
		}
	);
	/******************************************* HANDLERS *******************************************/
	const handleDiscardChanges = () => {
		const cacheMappedPayItems = queryClient.getQueryData<ISystemSettingsMappedPayItem[]>([
			PAYROLL_MAPPING_KEYS.QUERY.MAPPED_PAY_ITEMS,
			locationId,
		]);
		setAllMappedPayItems(cacheMappedPayItems ?? []);
	};

	const handleApplyMapping = () => {
		applyMapping(
			allMappedPayItems.map(({ _id, effectOnPay, ...rest }) => ({
				...rest,
			}))
		);
	};

	const payItemsSection = [
		...getSubmissionPayItems(allMappedPayItems),
		{
			id: "ingestible-pay-items",
			title: "Ingestible Pay Items",
			mappedPayItems: allMappedPayItems.filter((item) => item.type === "IN"),
		},
	];
	/******************************************* HELPERS *******************************************/
	// Helper function to compare original and current mapped pay items
	const hasStateChanged = useMemo(() => {
		const originalMappedPayItems = queryClient.getQueryData<ISystemSettingsMappedPayItem[]>([
			PAYROLL_MAPPING_KEYS.QUERY.MAPPED_PAY_ITEMS,
			locationId,
		]);
		if (originalMappedPayItems?.length !== allMappedPayItems.length) {
			return true;
		}
		return originalMappedPayItems.some((originalItem, index) => {
			const currentItem = allMappedPayItems[index];
			if (!currentItem) {
				return true;
			}

			// Compare basic properties
			if (originalItem.systemPayItem !== currentItem.systemPayItem) {
				return true;
			}
			// Compare payrollPayItems arrays
			if (originalItem.payrollPayItems?.length !== currentItem.payrollPayItems?.length) {
				return true;
			}
			return originalItem.payrollPayItems?.some((originalPayItem, payIndex) => {
				const currentPayItem = currentItem.payrollPayItems[payIndex];
				if (!currentPayItem) {
					return true;
				}
				return (
					originalPayItem.displayId !== currentPayItem.displayId ||
					originalPayItem.companyId !== currentPayItem.companyId ||
					originalPayItem.componentId !== currentPayItem.componentId ||
					originalPayItem.name !== currentPayItem.name
				);
			});
		});
	}, [allMappedPayItems]);

	/******************************************* EFFECTS *******************************************/
	useEffect(() => {
		if (mappedPayItems?.length) {
			setAllMappedPayItems(mappedPayItems);
		}
	}, [payItemsOptions, mappedPayItems, isActiveTab, locationId]);

	// Initialize updatedAt only once on first load
	useEffect(() => {
		if (payItemsOptions?.length && !updatedAt) {
			// Find the latest updatedAt value from payItemsOptions
			const latestDate = payItemsOptions.reduce((latest: string | null, item) => {
				if (!item.updatedAt) {
					return latest;
				}
				if (!latest) {
					return item.updatedAt;
				}
				return new Date(item.updatedAt) > new Date(latest) ? item.updatedAt : latest;
			}, null);
			if (latestDate) {
				setUpdatedAt(latestDate);
			}
		}
	}, [payItemsOptions, updatedAt]);

	const isLoading = isLoadingPayItemOption || isLoadingMappedPayItems;
	if (isLoading) {
		return <PayItemsSkeleton />;
	}

	const disabled = !editAccess || isPendingApplyingMapping;
	const allInputsFulfilled = areAllInputsFulfilled(allMappedPayItems);

	const getLastUpdatedDate = (date: string, timeZone: string | undefined, dateFormat = DATE_FORMAT.usDateTime24hSeconds) => {
		if (date) {
			return getLocalDate(date, dateFormat, timeZone) ?? "";
		}
		return null;
	};

	/******************************************* SUBSCRIPTIONS *******************************************/
	return (
		<Stack minHeight={"540px"} justifyContent={"space-between"}>
			<Box>
				{/* Header Section*/}
				<HeaderSection
					disabled={{
						discardButton: disabled || !hasStateChanged,
						applyButton: disabled || !allInputsFulfilled || !hasStateChanged,
					}}
					isSaving={isPendingApplyingMapping}
					handleDiscardChanges={handleDiscardChanges}
					handleApplyMapping={handleApplyMapping}
				/>
				{/* Mapping Section */}
				{payItemsSection.map((section, index) => (
					<Fragment key={section.id}>
						<MappingSection
							title={section.title}
							payItemsOptions={payItemsOptions ?? []}
							disabled={disabled}
							mappedPayItems={section.mappedPayItems}
							allMappedPayItems={allMappedPayItems}
							setMappedPayItems={setAllMappedPayItems}
						/>
						{index < payItemsSection.length - 1 && <Divider sx={{ mb: 4, mt: 3 }} />}
					</Fragment>
				))}
			</Box>
			<Box>
				<Stack direction={"row"} alignItems={"flex-start"} mt={5}>
					<CountDownButton
						disabled={isUpdatingPayItems || disabled}
						showButton
						typographySx={{ fontSize: "12px" }}
						initialCopy={"Update Now"}
						actionCopy="Updating..."
						loading={isUpdatingPayItems}
						counter={60}
						onClickCallBack={() => updatePayItemsOptionsMutation()}
					/>
					<TextIconMessage
						showContent
						title={"Last Updated on"}
						content={updatedAt ? getLastUpdatedDate(updatedAt, timezone, DATE_FORMAT.usDateTime12hUnpadded) : ""}
						loading={isLoading}
						sx={{
							marginLeft: "10px",
							color: "var(--text-color)",
						}}
					/>
				</Stack>
			</Box>
		</Stack>
	);
}
